import { useCallback, useEffect, useState } from 'react';
import { getCredBasicInfo, getCredPayoutAccount } from 'api/credentialing';
import type { TCredentialingProvider } from 'types/credentialing';

type TCredSetupStatus = {
    // fax 缺失：solo 看自己；group 任一 provider 缺 fax 即算缺（2026-08-26 确认）
    faxMissing: boolean;
    payoutDone: boolean;
    setPayoutDone: (done: boolean) => void;
    refetchPayout: () => void;
};

// submitted 页与 Credentialing status 页共用的「首次理赔前两项准备」状态：
// fax 号码（payer 入组前提）与 payout account（回款账户）。两边规则必须一致，
// 否则 submitted 页的 fax 卡和 status 页的提醒条会出现一个显示一个不显示。
const useCredSetupStatus = (isGroup: boolean, providers: TCredentialingProvider[]): TCredSetupStatus => {
    const [faxMissing, setFaxMissing] = useState(false);
    const [payoutDone, setPayoutDone] = useState(false);

    const fetchPayout = useCallback(async (onDone?: (done: boolean) => void) => {
        try {
            const res: any = await getCredPayoutAccount();
            onDone?.(!!res?.data?.data?.accountNumber);
        } catch (e) {
            // 无账户 / 请求失败都按「未设置」处理
            onDone?.(false);
        }
    }, []);

    const refetchPayout = useCallback(() => {
        fetchPayout(setPayoutDone);
    }, [fetchPayout]);

    useEffect(() => {
        let cancelled = false;
        fetchPayout((done) => {
            if (!cancelled) setPayoutDone(done);
        });
        return () => {
            cancelled = true;
        };
    }, [fetchPayout]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (isGroup && providers.length) {
                    const results = await Promise.all(
                        providers.map(async (prov) => {
                            try {
                                const res: any = await getCredBasicInfo(prov.providerId);
                                return !!res?.data?.data?.fax;
                            } catch {
                                return true; // 查询失败不触发推销
                            }
                        }),
                    );
                    if (!cancelled) setFaxMissing(results.some((hasFax) => !hasFax));
                } else if (!isGroup) {
                    const res: any = await getCredBasicInfo();
                    if (!cancelled) setFaxMissing(!res?.data?.data?.fax);
                }
            } catch (e) {
                // ignore
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isGroup, providers]);

    return { faxMissing, payoutDone, setPayoutDone, refetchPayout };
};

export default useCredSetupStatus;
